// @flow

import { all, spawn, put, call, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";

import hierarchySagas from "./hierarchy";
import absenceReviewSagas from "./absence-review";
import absenceReasonsSagas from "./absence-reasons";
import alertsSagas from "./alerts";
import employeeAbsencesSagas from "./employee-absences";
import employeeNotificationsSagas from "./employee-notifications";
import shiftPatternSagas from "./shift-patterns";
import staffGroupsSagas from "./staff-groups";
import serviceSectionStatus from "./service-section-statuses";
import employeePositionsSagas from "./employee-positions";
import specialContactTypes from "./special-contact-types";
import profileActionCreators from "@modules/profile/actionCreator";

import sharedActionCreators from "@modules/shared/actionCreator";
import {
  GetEmployeesList,
  GetEmployeePositionsList,
  SaveEmployeeRecordAccess,
  SaveAbsenceRecordAccess,
  SearchNotificationAuditByStatus,
  UpdateNotificationAuditStatus,
  NotificationAuditRemoveAccessToRecord,
} from "@modules/shared/constants/actions";
import { OPERATORS, transformFilters } from "@transformers/request";
import config from "@config";

// Common saga actions
export function* onGetEmployeesList({
  payload,
}: Action<{ clientId: number, page: number, filters: Object }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          page: payload.page,
          limit: config.resultsPerPage,
          search: transformFilters({ ...payload.filters, is_active: true } || {}, {
            last_name: OPERATORS.LIKE,
            employee_ref_primary: OPERATORS.LIKE,
            date_of_birth: OPERATORS.EQUALS,
          }),
        },
      },
    );
    yield put(sharedActionCreators.GetEmployeesListSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.GetEmployeesListError(e.response));
  }
}
export function* onGetEmployeePositionsList({
  payload,
}: Action<{ clientId: number, employeeId: number, page: number }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.get,
      `api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      {
        params: {
          page: payload.page,
          limit: config.resultsPerPage,
        },
      },
    );
    yield put(sharedActionCreators.GetEmployeePositionsListSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.GetEmployeePositionsListError(e.response));
  }
}

export function* onSaveEmployeeRecordAccess({
  payload,
}: Action<{ clientId: number, employeeId: number }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-record`,
    );
    yield put(sharedActionCreators.SaveEmployeeRecordAccessSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.SaveEmployeeRecordAccessError(e.response));
  }
}
export function* onSaveAbsenceRecordAccess({
  payload,
}: Action<{ clientId: number, employeeId: number, absenceId: number }>): Generator<
  *,
  void,
  Object,
> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
      throw Error("Invalid payload provided");
    }
    const { clientId, employeeId, absenceId } = payload;
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/access-record`,
    );
    yield put(sharedActionCreators.SaveEmployeeRecordAccessSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.SaveEmployeeRecordAccessError(e.response));
  }
}
export function* onSearchNotificationAuditByStatus({
  payload,
}: Action<{ managerId: number, clientId: number, status: [string] }>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.managerId || !payload.clientId || !payload.status) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.get,
      `/api/v1/my-details/client/${payload.clientId}/notification/audit`,
      {
        params: {
          search: transformFilters(
            { status: payload.status, manager_id: payload.managerId },
            {
              status: OPERATORS.EQUALS,
              manager_id: OPERATORS.EQUALS,
            },
          ),
        },
      },
    );
    yield put(sharedActionCreators.SearchNotificationAuditByStatusSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.SearchNotificationAuditByStatusError(e.response));
  }
}

export function* onUpdateNotificationAuditStatus({
  payload,
}: Action<{
  clientId: number,
  auditId: number,
  managerId: number,
  status: "ONGOING" | "COMPLETED",
}>): Generator<*, void, Object> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.auditId ||
      !payload.managerId ||
      !payload.status
    ) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/my-details/client/${payload.clientId}/notification/audit/${payload.auditId}`,
      {
        id: payload.auditId,
        manager: {
          id: payload.managerId,
        },
        status: payload.status,
      },
    );
    yield put(
      sharedActionCreators.SearchNotificationAuditByStatus({
        managerId: payload.managerId,
        clientId: payload.clientId,
        status: ["ONGOING", "PENDING"],
      }),
    );
    yield put(profileActionCreators.GetNotificationContacts({}));
    yield put(sharedActionCreators.UpdateNotificationAuditStatusSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.UpdateNotificationAuditStatusError(e.response));
  }
}

export function* onNotificationAuditRemoveAccessToRecord({
  payload,
}: Action<{ clientId: number, auditId: number, individualNotificationId: number }>): Generator<
  *,
  void,
  Object,
> {
  try {
    if (!payload || !payload.clientId || !payload.auditId || !payload.individualNotificationId) {
      throw Error("Invalid payload provided");
    }
    const { clientId, auditId, individualNotificationId } = payload;

    const response = yield call(
      http.post,
      `/api/v1/my-details/client/${clientId}/notification/audit/${auditId}/action`,
      {
        operation: "DELETE",
        individual_notification_id: individualNotificationId,
        notification_audit_id: auditId,
      },
    );
    yield put(sharedActionCreators.NotificationAuditRemoveAccessToRecordSuccess(response.data));
    yield put(profileActionCreators.GetNotificationContacts({}));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(sharedActionCreators.NotificationAuditRemoveAccessToRecordError(e.response));
  }
}

export function* SharedSaga(): Generator<*, void, void> {
  yield all([
    spawn(hierarchySagas),
    spawn(absenceReviewSagas),
    spawn(absenceReasonsSagas),
    spawn(alertsSagas),
    spawn(employeeAbsencesSagas),
    spawn(employeeNotificationsSagas),
    spawn(shiftPatternSagas),
    spawn(staffGroupsSagas),
    spawn(serviceSectionStatus),
    spawn(employeePositionsSagas),
    spawn(specialContactTypes),
  ]);
  yield takeEvery(GetEmployeesList, onGetEmployeesList);
  yield takeEvery(GetEmployeePositionsList, onGetEmployeePositionsList);
  yield takeEvery(SaveEmployeeRecordAccess, onSaveEmployeeRecordAccess);
  yield takeEvery(SaveAbsenceRecordAccess, onSaveAbsenceRecordAccess);
  yield takeEvery(NotificationAuditRemoveAccessToRecord, onNotificationAuditRemoveAccessToRecord);
  yield takeEvery(SearchNotificationAuditByStatus, onSearchNotificationAuditByStatus);
  yield takeEvery(UpdateNotificationAuditStatus, onUpdateNotificationAuditStatus);
}
export default SharedSaga;
